<template>
  <v-breadcrumbs v-bind:items="breadcrumbItems" class="pl-0 mx-1 overflow-x-auto overflow-y-hidden" data-cy="topbar-breadcrumbs">
    <template v-slot:divider>
      <v-icon class="mr-0">mdi-chevron-right</v-icon>
    </template>

    <template v-slot:item="{ item }">
      <v-breadcrumbs-item class="breadcrumb-item">
        <!-- Non-editable items -->
        <span v-if="!item.disabled && !item.isEditable" class="breadcrumb-item-text">
          <v-icon v-if="item.icon" size="small">{{ item.icon }}</v-icon>
          <router-link v-bind:to="{ path: item.href }" class="breadcrumb-text-truncate">
            {{ item.text }}
          </router-link>
        </span>

        <!-- Editable last item (current folder) -->
        <span v-if="item.disabled && item.isEditable && !isEditingName" class="breadcrumb-item-text">
          <span class="breadcrumb-editable" @click="startEditingName(item)">
            <v-icon v-if="item.icon" size="small">{{ item.icon }}</v-icon>
            <span class="breadcrumb-text-truncate">{{ item.text }}</span>
            <v-icon class="edit-icon ml-1" size="small">mdi-pencil</v-icon>
          </span>
        </span>

        <!-- Edit mode for last item -->
        <span v-if="item.disabled && item.isEditable && isEditingName && editingItemId === item.id" class="breadcrumb-item-text">
          <v-form ref="renameForm" @submit.prevent="saveNameChange">
            <v-text-field
              v-model="editedName"
              :rules="nodeNameRules"
              density="compact"
              variant="solo-filled"
              hide-details="auto"
              flat
              autofocus
              class="breadcrumb-name-input"
              @blur="handleBlur"
              @keydown.esc="cancelEditingName"
            />
          </v-form>
        </span>

        <!-- Non-editable disabled items -->
        <span v-if="item.disabled && !item.isEditable" class="text-truncate breadcrumb-item-text" >
          <v-icon v-if="item.icon" size="small">{{ item.icon }}</v-icon>
          {{ item.text }}
        </span>
      </v-breadcrumbs-item>
    </template>
  </v-breadcrumbs>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore';
import { useNodeStore } from '@/stores/NodeStore';
import { usePodStore } from '@/stores/PodStore';

export default {
  name: 'BreadcrumbNodes',
  data() {
    return {
      isEditingName: false,
      editedName: '',
      editingItemId: null,
      nodeNameRules: [
        (v) => !!v || this.$t('routes.node.requiredName'),
        (v) => v && v.length <= 191 || this.$t('routes.node.folderNameTooLong'),
      ],
    };
  },
  methods: {
    getPodItem(pod) {
      return {
        text: pod.name,
        icon: 'mdi-file-outline',
      };
    },
    getNodeItem(node) {
      const item = {
        id: node.id,
        parentid: node.parentid,
        text: node.name,
        href: '/node/' + node.id,
        disabled: false,
        icon: this.getNodeIcon(node),
      };

      return item;
    },
    getSeparationItem() {
      return {
        text: '',
        disabled: true,
        icon: 'mdi-dots-horizontal',
      };
    },
    getNodeIcon(node) {
      if (node.archived === 1 || node.archived === 2) {
        return 'mdi-package-down';
      }

      switch (node.type) {
        case 1: {
          if (node.teamonly === 1) {
            return 'mdi-folder-account';
          } else {
            return 'mdi-folder';
          }
        }
        case 2: return 'mdi-briefcase-clock';
        case 3: return 'tw tw-office-building';
        case 4: return 'mdi-home-assistant';
        default: return 'mdi-folder';
      }
    },
    startEditingName(item) {
      if (!item.id) {
        return;
      }

      this.isEditingName = true;
      this.editedName = item.text;
      this.editingItemId = item.id;
    },
    cancelEditingName() {
      this.isEditingName = false;
      this.editedName = '';
      this.editingItemId = null;
    },
    async handleBlur() {
      const currentNode = this.currentNode;
      const trimmedName = this.editedName.trim();

      if (!currentNode || trimmedName === currentNode.name) {
        this.cancelEditingName();
        return;
      }

      if (!this.$refs.renameForm) {
        this.cancelEditingName();
        return;
      }

      const validForm = await this.$refs.renameForm.validate();

      if (!validForm.valid) {
        this.cancelEditingName();
        return;
      }

      this.saveNameChange();
    },
    async saveNameChange() {
      if (!this.$refs.renameForm) {
        return;
      }

      const validForm = await this.$refs.renameForm.validate();

      if (!validForm.valid) {
        return;
      }

      const currentNode = this.currentNode;
      const trimmedName = this.editedName.trim();

      if (!currentNode || trimmedName === currentNode.name) {
        this.cancelEditingName();
        return;
      }

      const payload = {
        id: currentNode.id,
        name: trimmedName,
      };

      const nodeStore = useNodeStore();

      try {
        await nodeStore.renameNode(payload);
        this.$showMessage(this.$t('routes.node.folderRenamed'), { type: 'success' });
        this.cancelEditingName();
      } catch (error) {
        this.cancelEditingName();

        if (error?.response?.data?.error?.message) {
          this.$showMessage(error.response.data.error.message, { type: 'error' });
        } else {
          this.$showMessage('Error!', { type: 'error' });
        }

        console.log(error);
      }
    },
  },
  computed: {
    getNodeById() {
      return (nodeId) => {
        const nodeStore = useNodeStore();
        return nodeStore.getNodeById(nodeId);
      };
    },
    currentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    getActiveAccessPoint() {
      const authStore = useAuthStore();
      return authStore.getActiveAccesspoint;
    },
    pod() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    breadcrumbItems() {
      const items = [];

      if (this.currentNode) {
        items.unshift(this.getNodeItem(this.currentNode));

        const parentNode = this.getNodeById(this.currentNode.parentid);
        if (parentNode) {
          items.unshift(this.getNodeItem(parentNode));

          const parentOfParent = this.getNodeById(parentNode.parentid);
          if (parentOfParent) {
            items.unshift(this.getNodeItem(parentOfParent));
          }
        }
      } else if (this.pod) {
        items.unshift(this.getPodItem(this.pod));
      }

      if (!this.getActiveAccessPoint) {
        return [];
      }

      const contextNodeId = this.getActiveAccessPoint.node.id;
      const contextNode = this.getActiveAccessPoint.node;

      const alreadyAdded = items.length > 0 && items[0].id == contextNodeId;

      if (!alreadyAdded) {
        while (items.length > 3) {
          items.shift();
        }

        if (contextNode && items.length > 0 && items[0].parentid != contextNodeId) {
          items.unshift(this.getSeparationItem());
        }

        if (contextNode) {
          items.unshift(this.getNodeItem(contextNode));
        }
      }

      if (items.length > 1) {
        items[items.length - 1].disabled = true;

        // Mark the last item as editable if it's a node (has an id)
        if (items[items.length - 1].id) {
          items[items.length - 1].isEditable = true;
        }
      }

      return items;
    },
  },
};
</script>

<style scoped>
.breadcrumb-item a {
  text-decoration: none;

  .v-theme--dark & {
    color: #EBEBEB !important;
  }

  .v-theme--light & {
    color: #131313 !important;
  }
}

.breadcrumb-item-text {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  max-width: 400px;
}

.breadcrumb-text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
  padding-left: 3px;
  padding-bottom: 1px;
}

.breadcrumb-editable {
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: 1px dashed transparent;
  border-radius: 4px;
  transition: border-color 0.2s ease;
  cursor: pointer;
  user-select: none;

  &:hover {
    border-color: rgba(128, 128, 128, 0.5);

    & .edit-icon {
      opacity: 1;
    }
  }
}


.edit-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.breadcrumb-name-input {
  display: inline-block;
  min-width: 200px;
  max-width: 400px;
  vertical-align: middle;

  & :deep(.v-field__input) {
    padding: 4px 8px;
    min-height: auto;
  }

  & :deep(.v-field) {
    border: 1px dashed rgba(128, 128, 128, 0.5);
    border-radius: 4px;
  }

  & :deep(.v-field--error) {
    border-color: rgb(var(--v-theme-error));
  }
}

@media only screen and (max-width: 960px) {
  .breadcrumb-text-truncate {
    max-width: 120px;
  }

  .breadcrumb-name-input {
    min-width: 120px;
    max-width: 200px;
  }
}

@media only screen and (max-width: 600px) {
  .v-breadcrumbs {
    padding: 0 !important;
  }
}
</style>
